import { z } from 'zod';

import { CurrencyDenominationSchema } from './equipment';

/** Uppercase slug for platform catalog entities (MOEDA uses ISO 4217 separately). */
export const PlatformCatalogCodeSchema = z
  .string()
  .regex(/^[A-Z0-9_]{2,32}$/);

export const Iso4217CodeSchema = z.string().length(3).regex(/^[A-Z]{3}$/);

// ─── Moeda ───────────────────────────────────────────────────────────────────

export const MoedaSchema = z.object({
  codigo: Iso4217CodeSchema,
  nome: z.string().min(1).max(120),
  simbolo: z.string().min(1).max(8),
  decimais: z.number().int().min(0).max(4),
  ativo: z.boolean(),
  denominacoesAceitas: z.array(CurrencyDenominationSchema).optional(),
  systemLocked: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type Moeda = z.infer<typeof MoedaSchema>;

export const MoedaCreateSchema = MoedaSchema.omit({
  createdAt: true,
  updatedAt: true,
  updatedBy: true,
  systemLocked: true,
}).extend({
  systemLocked: z.boolean().optional(),
});

export type MoedaCreateInput = z.infer<typeof MoedaCreateSchema>;

export const MoedaPatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    simbolo: z.string().min(1).max(8).optional(),
    decimais: z.number().int().min(0).max(4).optional(),
    ativo: z.boolean().optional(),
    denominacoesAceitas: z.array(CurrencyDenominationSchema).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type MoedaPatchInput = z.infer<typeof MoedaPatchSchema>;

// ─── Tipo Pagamento ──────────────────────────────────────────────────────────

export const TipoPagamentoSchema = z.object({
  codigo: PlatformCatalogCodeSchema,
  nome: z.string().min(1).max(120),
  moedaCodigo: Iso4217CodeSchema,
  ativo: z.boolean(),
  systemLocked: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type TipoPagamento = z.infer<typeof TipoPagamentoSchema>;

export const TipoPagamentoCreateSchema = TipoPagamentoSchema.omit({
  createdAt: true,
  updatedAt: true,
  updatedBy: true,
  systemLocked: true,
});

export type TipoPagamentoCreateInput = z.infer<typeof TipoPagamentoCreateSchema>;

export const TipoPagamentoPatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    moedaCodigo: Iso4217CodeSchema.optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type TipoPagamentoPatchInput = z.infer<typeof TipoPagamentoPatchSchema>;

// ─── Tipo Operação ───────────────────────────────────────────────────────────

export const DirecaoOperacaoSchema = z.enum(['ENTRADA', 'SAIDA', 'BAAS']);
export type DirecaoOperacao = z.infer<typeof DirecaoOperacaoSchema>;

export const RequerAprovacaoSchema = z.enum(['NAO', 'SIM', 'AUTO']);
export type RequerAprovacao = z.infer<typeof RequerAprovacaoSchema>;

export const TipoOperacaoSchema = z.object({
  codigo: PlatformCatalogCodeSchema,
  nome: z.string().min(1).max(120),
  direcao: DirecaoOperacaoSchema,
  requerAprovacao: RequerAprovacaoSchema,
  ativo: z.boolean(),
  systemLocked: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type TipoOperacao = z.infer<typeof TipoOperacaoSchema>;

export const TipoOperacaoCreateSchema = TipoOperacaoSchema.omit({
  createdAt: true,
  updatedAt: true,
  updatedBy: true,
  systemLocked: true,
});

export type TipoOperacaoCreateInput = z.infer<typeof TipoOperacaoCreateSchema>;

export const TipoOperacaoPatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    direcao: DirecaoOperacaoSchema.optional(),
    requerAprovacao: RequerAprovacaoSchema.optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type TipoOperacaoPatchInput = z.infer<typeof TipoOperacaoPatchSchema>;

// ─── Sensor ──────────────────────────────────────────────────────────────────

export const TipoSensorSchema = z.enum(['BINARIO', 'MULTISTATUS', 'NUMERICO']);
export type TipoSensor = z.infer<typeof TipoSensorSchema>;

export const LimiarOperadorSchema = z.enum(['GT', 'LT', 'GTE', 'LTE', 'EQ']);

export const LimiarSensorSchema = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('DURACAO'),
    valorSegundos: z.number().int().positive(),
    descricaoDisplay: z.string().min(1),
  }),
  z.object({
    tipo: z.literal('ESTADO'),
    valor: z.string().min(1),
    descricaoDisplay: z.string().min(1),
  }),
  z.object({
    tipo: z.literal('NUMERICO'),
    operador: LimiarOperadorSchema,
    valor: z.number(),
    unidade: z.string().max(16).optional(),
    descricaoDisplay: z.string().min(1),
  }),
  z.object({
    tipo: z.literal('CODIGO'),
    valor: z.string().min(1),
    descricaoDisplay: z.string().min(1),
  }),
]);

export type LimiarSensor = z.infer<typeof LimiarSensorSchema>;

export const SensorDefinitionSchema = z.object({
  codigo: PlatformCatalogCodeSchema,
  nome: z.string().min(1).max(120),
  tipoSensor: TipoSensorSchema,
  unidade: z.string().max(16).optional(),
  limiarAlerta: LimiarSensorSchema,
  limiarCritico: LimiarSensorSchema,
  aplicaSemCofre: z.boolean().default(false),
  ativo: z.boolean(),
  systemLocked: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type SensorDefinition = z.infer<typeof SensorDefinitionSchema>;

export const SensorDefinitionCreateSchema = SensorDefinitionSchema.omit({
  createdAt: true,
  updatedAt: true,
  updatedBy: true,
  systemLocked: true,
});

export type SensorDefinitionCreateInput = z.infer<typeof SensorDefinitionCreateSchema>;

export const SensorDefinitionPatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    tipoSensor: TipoSensorSchema.optional(),
    unidade: z.string().max(16).optional(),
    limiarAlerta: LimiarSensorSchema.optional(),
    limiarCritico: LimiarSensorSchema.optional(),
    aplicaSemCofre: z.boolean().optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type SensorDefinitionPatchInput = z.infer<typeof SensorDefinitionPatchSchema>;

// ─── Layout Equipamento ──────────────────────────────────────────────────────

export const LayoutEquipamentoSchema = z.object({
  layoutId: z.string().uuid(),
  nome: z.string().min(1).max(120),
  svgS3Key: z.string().min(1),
  ativo: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type LayoutEquipamento = z.infer<typeof LayoutEquipamentoSchema>;

export const LayoutEquipamentoCreateSchema = z.object({
  nome: z.string().min(1).max(120),
  svgMarkup: z.string().min(1).max(65_536),
});

export type LayoutEquipamentoCreateInput = z.infer<typeof LayoutEquipamentoCreateSchema>;

export const LayoutEquipamentoPatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    svgMarkup: z.string().min(1).max(65_536).optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type LayoutEquipamentoPatchInput = z.infer<typeof LayoutEquipamentoPatchSchema>;

// ─── LED Profile ─────────────────────────────────────────────────────────────

export const LedAnimacaoSchema = z.enum(['FIXO', 'PISCANTE', 'SEQUENCIAL']);
export type LedAnimacao = z.infer<typeof LedAnimacaoSchema>;

export const HexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

export const LedEstadoSchema = z.object({
  estado: z.string().min(1),
  cor: HexColorSchema,
  label: z.string().min(1),
  animacao: LedAnimacaoSchema,
  editavel: z.boolean().default(true),
});

export type LedEstado = z.infer<typeof LedEstadoSchema>;

export const LedProfileSchema = z.object({
  profileId: z.string().uuid(),
  nome: z.string().min(1).max(120),
  corNormal: HexColorSchema,
  isDefault: z.boolean(),
  estados: z.array(LedEstadoSchema).min(1),
  ativo: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type LedProfile = z.infer<typeof LedProfileSchema>;

export const LedProfileCreateSchema = z.object({
  nome: z.string().min(1).max(120),
  corNormal: HexColorSchema,
  isDefault: z.boolean().optional(),
  estados: z.array(LedEstadoSchema).min(1).optional(),
});

export type LedProfileCreateInput = z.infer<typeof LedProfileCreateSchema>;

export const LedProfilePatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    corNormal: HexColorSchema.optional(),
    isDefault: z.boolean().optional(),
    estados: z.array(LedEstadoSchema).min(1).optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type LedProfilePatchInput = z.infer<typeof LedProfilePatchSchema>;

/** System-fixed LED colors — not customizable via portal (RN-PLATFORM-001). */
export const SYSTEM_LED_COLORS = {
  alerta: '#F0A020',
  critico: '#F04040',
  suspenso: '#8060D0',
  ota: '#4A90D0',
} as const;

/**
 * Derives the standard 6-state LED mapping from a brand primary color.
 * Matches prototype `renderLEDPreview` behavior in docs/prototype.html.
 */
export function buildLedEstadosFromCorNormal(corNormal: string): LedEstado[] {
  const parsed = HexColorSchema.parse(corNormal);

  return [
    { estado: 'Normal', cor: parsed, label: 'Cor da marca · fixo', animacao: 'FIXO', editavel: true },
    { estado: 'Depósito ativo', cor: parsed, label: 'Cor da marca · piscante', animacao: 'PISCANTE', editavel: true },
    { estado: 'Alerta', cor: SYSTEM_LED_COLORS.alerta, label: 'Laranja · sistema (fixo)', animacao: 'PISCANTE', editavel: false },
    { estado: 'Crítico / Offline', cor: SYSTEM_LED_COLORS.critico, label: 'Vermelho · sistema (fixo)', animacao: 'PISCANTE', editavel: false },
    { estado: 'Suspenso', cor: SYSTEM_LED_COLORS.suspenso, label: 'Roxo · sistema (fixo)', animacao: 'FIXO', editavel: false },
    { estado: 'OTA em andamento', cor: SYSTEM_LED_COLORS.ota, label: 'Azul · sistema (fixo)', animacao: 'SEQUENCIAL', editavel: false },
  ];
}

// ─── Variante Hardware ───────────────────────────────────────────────────────

export const VarianteHardwareSchema = z.object({
  codigo: PlatformCatalogCodeSchema,
  nome: z.string().min(1).max(120),
  descricao: z.string().max(500).optional(),
  ativo: z.boolean(),
  systemLocked: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().min(1),
});

export type VarianteHardware = z.infer<typeof VarianteHardwareSchema>;

export const VarianteHardwareCreateSchema = VarianteHardwareSchema.omit({
  createdAt: true,
  updatedAt: true,
  updatedBy: true,
  systemLocked: true,
});

export type VarianteHardwareCreateInput = z.infer<typeof VarianteHardwareCreateSchema>;

export const VarianteHardwarePatchSchema = z
  .object({
    nome: z.string().min(1).max(120).optional(),
    descricao: z.string().max(500).optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type VarianteHardwarePatchInput = z.infer<typeof VarianteHardwarePatchSchema>;
